import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Loading from "../../Common/Loading";
import VbGrid, { VbGridColumn } from "../../Common/Vbgrid";
import CustomAlert from "../../Common/CustomAlert"

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

// VB: btnActionTaken has Visible="false" - keep it hidden for parity.
const SHOW_ACTION_TAKEN = false;

type Row = Record<string, string>;

// VB Header / Align / ColsWidth arrays. InnerException has Align "" in VB, i.e. it is a HIDDEN column.
const FIELDS: { key: string; col: VbGridColumn }[] = [
  { key: "ID", col: { header: "ID", align: "right", type: "N" } },
  { key: "LoggedOn", col: { header: "OccuredAt", align: "left", type: "D" } },
  { key: "SchoolCode", col: { header: "SchoolCode", align: "left", type: "S" } },
  { key: "FamilyCode", col: { header: "FamilyCode", align: "left", type: "S" } },
  { key: "LoggedBy", col: { header: "Login", align: "left", type: "S" } },
  { key: "Category", col: { header: "Category", align: "left", type: "S" } },
  { key: "SessionID", col: { header: "SessionID", align: "left", type: "S" } },
  { key: "ErrorNo", col: { header: "ErrorNo", align: "left", type: "S" } },
  { key: "ErrorMessage", col: { header: "Error Message", align: "left", width: 200, type: "S" } },
  { key: "StackTrace", col: { header: "StackTrace", align: "left", type: "S", html: true } },
  { key: "SesVars", col: { header: "Session Variables", align: "left", type: "S" } },
  { key: "InnerException", col: { header: "InnerException", hidden: true, type: "S" } },
  { key: "UserAgent", col: { header: "UserAgent", align: "left", type: "S" } },
  { key: "Browser", col: { header: "Browser", align: "left", type: "S" } },
  { key: "OS", col: { header: "OS", align: "left", type: "S" } },
];
const GRID_COLUMNS = FIELDS.map((f) => f.col);

interface Filters {
  errorNo: string;
  errorMsg: string;
  maxRecords: string;
}

// ── VB helpers ────────────────────────────────────────────────────────────────
const vbVal = (s: string): number => {
  const m = s.match(/^\s*[-+]?(\d+\.?\d*|\.\d+)/);
  return m ? parseFloat(m[0]) : 0;
};
const roundHalfEven = (n: number): number => {
  const f = Math.floor(n);
  const d = n - f;
  if (d < 0.5) return f;
  if (d > 0.5) return f + 1;
  return f % 2 === 0 ? f : f + 1;
};

// gfn_ShowMessage types: E = error, I = information, A = warning (default), Q = confirmation
type MsgType = "E" | "I" | "A" | "Q" | "";

// Keep this helper, but do NOT pass it to CustomAlert
const formatAlertMessage = (msg: string, type: MsgType): string => {
  const prefix =
    type === "E" ? "Error" :
    type === "I" ? "Information" :
    type === "Q" ? "Confirmation" :
    "Warning";

  const safeMsg = (msg || "").replace(/\n/g, "<br/>");
  return `${prefix}:<br/>${safeMsg}`;
};

// ── CustomAlert adapter ───────────────────────────────────────────────────────
// If your CustomAlert uses different prop names / type values, change ONLY this mapping
// and the single <CustomAlert ... /> element near the bottom of the page.
const ALERT_TYPES: Record<MsgType, "error" | "info" | "warning"> = {
  E: "error", I: "info", A: "warning", Q: "info", "": "warning",
};

const EL_ShowErrorLog: React.FC = () => {
  const [data, setData] = useState<Row[]>([]);
  const [selIdx, setSelIdx] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState<MsgType>("");

  // txtErrorNo / txtErrorMessage / txtMaxRecords ("200" on first load) / txtRowsPerPage
  const [filters, setFilters] = useState<Filters>({ errorNo: "", errorMsg: "", maxRecords: "200" });
  const [rowsPerPageTxt, setRowsPerPageTxt] = useState("");

  // grid navigation state (VB fn_ShowErrorLog(1, 20, False) on first load)
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [startRec, setStartRec] = useState(1);
  const [gridSize, setGridSize] = useState({ w: 800, h: 400 });

  const filtersRef = useRef<HTMLTableElement>(null);
  const buttonsRef = useRef<HTMLTableElement>(null);

  // every message / alert on this page goes through here -> CustomAlert
  const showMsg = useCallback((msg: string, type: MsgType = "A") => {
    if (msg) { setMessage(msg); setMsgType(type); }
  }, []);

  // Equivalent of fn_ShowErrorLog(start, rowsPP, False): re-query and rebuild the grid
  // (selection and sort are lost, like a postback).
  const load = useCallback(async (f: Filters, rowsPP: number, start: number = 1) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}api/error-log/?errorNo=${encodeURIComponent(f.errorNo)}` +
        `&errorMsg=${encodeURIComponent(f.errorMsg)}` +
        `&maxRecords=${encodeURIComponent(f.maxRecords)}`
      );
      const json = await res.json();
      setData(json.data || []);
      setRowsPerPage(rowsPP);
      setStartRec(start);
      setSelIdx(null);
      if (json.errorMessage) showMsg(json.errorMessage, "E");
    } catch (e) {
      setData([]);
      showMsg(String(e), "E");
    } finally {
      setLoading(false);
    }
  }, [showMsg]);

  useEffect(() => {
    document.body.style.backgroundColor = "#fff";
  }, []);

  useEffect(() => {
    load({ errorNo: "", errorMsg: "", maxRecords: "200" }, 20, 1);
  }, [load]);

  // VB fn_Load: grid width = clientWidth-20, height = clientHeight - buttons - filters - 50
  useEffect(() => {
    const resize = () => {
      if (!filtersRef.current || !buttonsRef.current) return;
      const w = window.innerWidth;
      const h = window.innerHeight;
      filtersRef.current.style.width = w - 20 + "px";
      setGridSize({
        w: w - 20,
        h: Math.max(60, h - buttonsRef.current.offsetHeight - filtersRef.current.offsetHeight - 50),
      });
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const gridRows = useMemo(
    () => data.map((r) => FIELDS.map((f) => r[f.key] ?? "")),
    [data]
  );

  const selected: Row | null = selIdx !== null ? data[selIdx] ?? null : null;

  // ── btnGO_Click ─────────────────────────────────────────────────────────────
  const handleSelect = () => {
    let rowsPP = 20;
    const t = rowsPerPageTxt.trim();
    if (t !== "" && roundHalfEven(vbVal(t)) > 0) rowsPP = roundHalfEven(vbVal(t));
    load(filters, rowsPP, 1);
  };

  // ── grid navigation (hidStartRecNo / hidRowsperPage postback -> Page_LoadComplete) ──
  const handleNavigate = (start: number, rowsPP: number) => {
    load(filters, rowsPP, start);
  };

  // ── fn_CheckRowSelected ─────────────────────────────────────────────────────
  const requireRow = (): Row | null => {
    if (!selected) {
      showMsg("Select a Row", "A");
      return null;
    }
    return selected;
  };

  const openDetails = (option: string) => {
    const row = requireRow();
    if (!row) return;
    const w = window.screen.availWidth;
    const h = window.screen.availHeight;
    window.open(
      `/WEBADMIN/REPORTS/EL_ShowErrorDetailsForID?ID=${encodeURIComponent(row.ID)}&Option=${encodeURIComponent(option)}`,
      "",
      `location=0,channelmode=1,directories=0,menubar=0,resizable=1,scrollbars=1,toolbar=0,status=1,left=0,top=0,width=${w - 10},height=${h - 80}`
    );
  };

  const handleStackTrace = () => {
    const row = requireRow();
    if (!row) return;
    showMsg(row.StackTrace || "", "I");
  };

  // ── btnGoTo_Click (T1236) ───────────────────────────────────────────────────
  const handleGoTo = async () => {
    const row = requireRow();
    if (!row) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/error-log/go-to/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: row.ID }),
      });
      const json = await res.json();
      // VB used JS alert() here; now shown with CustomAlert
      if (json.alerts && json.alerts.length > 0) showMsg(json.alerts.join("\n"), "I");
      if (json.openWindow) window.open(json.openWindow, "_blank");
      if (json.errorMessage) showMsg(json.errorMessage, "E");
    } catch (e) {
      showMsg(String(e), "E");
    }
    // VB always ends with: txtMaxRecords.Text = "200" : fn_ShowErrorLog(1, 20, False)
    const f = { ...filters, maxRecords: "200" };
    setFilters(f);
    await load(f, 20, 1);
  };

  // ── btnActionTaken_Click (hidden in VB) ─────────────────────────────────────
  const handleActionTaken = async () => {
    const row = requireRow();
    if (!row) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/error-log/action/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: row.ID }),
      });
      const json = await res.json();
      if (json.errorMessage) showMsg(json.errorMessage, "E");
    } catch (e) {
      showMsg(String(e), "E");
    }
    await load(filters, 20, 1);
  };

  return (
    <>
      <style>{`body { margin: 0; font-family: 'Times New Roman', serif; }`}</style>

      {loading && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 99998, background: "rgba(255,255,255,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Loading />
        </div>
      )}

      {/* all alerts / messages on this page */}
      {message && (
        <CustomAlert
          message={formatAlertMessage(message, msgType)}
          onClose={() => setMessage("")}
        />
      )}

      {/* ── tblFilters ── */}
      <table
        id="tblFilters"
        ref={filtersRef}
        cellPadding={5}
        cellSpacing={0}
        style={{ border: "solid 1px black", margin: 10, width: "calc(100% - 20px)" }}
      >
        <tbody>
          <tr>
            <td align="center" colSpan={2} style={{ backgroundColor: "#C7DDFD", width: "100%" }}>Error LOG</td>
          </tr>
          <tr>
            <td align="left">
              ErrorNo:{" "}
              <input
                value={filters.errorNo}
                onChange={(e) => setFilters({ ...filters, errorNo: e.target.value })}
                style={{ border: "solid 1px" }}
              />
              &nbsp;&nbsp;&nbsp;&nbsp;
              ErrorMessage:{" "}
              <input
                value={filters.errorMsg}
                onChange={(e) => setFilters({ ...filters, errorMsg: e.target.value })}
                style={{ border: "solid 1px" }}
              />
              &nbsp;&nbsp;&nbsp;&nbsp;
              Max.Records:{" "}
              <input
                value={filters.maxRecords}
                onChange={(e) => setFilters({ ...filters, maxRecords: e.target.value })}
                style={{ border: "solid 1px" }}
              />
              &nbsp;&nbsp;&nbsp;&nbsp;
              Records/Page:{" "}
              <input
                value={rowsPerPageTxt}
                onChange={(e) => setRowsPerPageTxt(e.target.value)}
                style={{ border: "solid 1px" }}
              />
            </td>
            <td align="right">
              <button onClick={handleSelect} disabled={loading}> SELECT </button>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── grid (err_) ── */}
      <div style={{ margin: 10 }}>
        <VbGrid
          columns={GRID_COLUMNS}
          rows={gridRows}
          selectable
          selectedIndex={selIdx}
          onSelect={setSelIdx}
          pagination={{
            startRec,
            rowsPerPage,
            onNavigate: handleNavigate,
            showRowsPerPage: true,
            showNullsBox: true,
          }}
          width={gridSize.w}
          height={gridSize.h}
        />
      </div>

      {/* ── tblButtons ── */}
      <table id="tblButtons" ref={buttonsRef} cellPadding={5} cellSpacing={0} style={{ width: "100%" }}>
        <tbody>
          <tr>
            <td align="left">
              <button onClick={() => (window.location.href = "/SysAdmin")} disabled={loading}>Back to Menu</button>
            </td>
            <td align="right">
              {/* <button onClick={handleGoTo} disabled={loading}>Go To StudyRoom</button> */}
              <button onClick={handleGoTo} disabled={true}>Go To StudyRoom</button>
              &nbsp;&nbsp;&nbsp;&nbsp;
              {SHOW_ACTION_TAKEN && (
                <>
                  <button onClick={handleActionTaken} disabled={loading}>Action Taken</button>
                  &nbsp;&nbsp;&nbsp;&nbsp;
                </>
              )}
              <button onClick={() => openDetails("UsageForSession")} disabled={loading}>Usage LOG - SessionID</button>
              &nbsp;&nbsp;&nbsp;&nbsp;
              <button onClick={() => openDetails("EventLog")} disabled={loading}>Event LOG</button>
              &nbsp;&nbsp;&nbsp;&nbsp;
              <button onClick={handleStackTrace} disabled={loading}>Stack Trace</button>
              &nbsp;&nbsp;&nbsp;&nbsp;
              <button onClick={() => openDetails("SesVars")} disabled={loading}>Session Variables</button>
              &nbsp;&nbsp;&nbsp;&nbsp;
              <button onClick={() => openDetails("Usage")} disabled={loading}>Usage LOG - User</button>
              &nbsp;&nbsp;&nbsp;&nbsp;
              <button onClick={() => openDetails("UserDiary")} disabled={loading}>User Diary</button>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default EL_ShowErrorLog;